import { type ReactElement, useState } from 'react';
import { useInvalidateQuotes, useQuoteDetailQuery } from '@/entities/quote';
import { Button, EmptyState } from '@/shared/ui';
import { QuoteDetailContent } from './quote-detail-content';
import { QuoteDetailHeader } from './quote-detail-header';

export interface QuoteDetailPanelProps {
  readonly quoteRequestId: string;
  /** 머리말 "목록으로" — 페이지가 견적 목록으로 옮긴다. */
  readonly onBack: () => void;
}

/**
 * 어드민 견적 상세(견적 설계 §6-2~6-7) — 상세를 읽어 머리말(견적 번호·상태 배지·목록으로)을 그리고, 받기 전·실패는 카드 안
 * 가운데 안내로 보인다. 받은 뒤의 본문(요청 내용·연락처·디자인·메모·발행본·초안 편집기·반려·주문 영역)은 QuoteDetailContent 가 그린다.
 * 이미 받은 상세가 있으면 배경 재조회가 실패해도 화면(편집 중인 초안 입력 포함)을 지우지 않고 다시 시도 띠만 띄운다.
 * 편집기는 초안 id 와 "다시 불러오기" 횟수(editorEpoch)를 key 로 삼아, 운영자가 고를 때만 새 초안으로 다시 시작한다.
 */
export const QuoteDetailPanel = ({
  quoteRequestId,
  onBack,
}: QuoteDetailPanelProps): ReactElement => {
  const detailQuery = useQuoteDetailQuery(quoteRequestId);
  const invalidate = useInvalidateQuotes();
  const [editorEpoch, setEditorEpoch] = useState(0);
  const quote = detailQuery.data;

  const handleReloadLatest = (): void => {
    void invalidate.detail(quoteRequestId).then(() => setEditorEpoch((epoch) => epoch + 1));
  };

  let body: ReactElement;
  if (detailQuery.isPending) {
    body = <EmptyState>불러오는 중…</EmptyState>;
  } else if (quote === undefined) {
    // 알림(alert)은 오류 글만 읽게 하고, 다시 시도 버튼은 안내 상자 아래 형제로 둔다.
    body = (
      <div className="flex flex-col gap-4">
        <EmptyState isError>견적을 불러오지 못했습니다: {detailQuery.error?.message}</EmptyState>
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void detailQuery.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      </div>
    );
  } else {
    body = (
      <QuoteDetailContent
        quote={quote}
        editorEpoch={editorEpoch}
        onReloadLatest={handleReloadLatest}
        error={
          detailQuery.isError
            ? '최신 내용을 불러오지 못했습니다. 지금 화면은 마지막으로 받은 내용입니다.'
            : null
        }
        onReload={() => {
          void detailQuery.refetch();
        }}
      />
    );
  }

  return (
    <>
      <QuoteDetailHeader quote={quote ?? null} onBack={onBack} />
      {body}
    </>
  );
};
